import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Lock } from "lucide-react";
import { IMAGES, INSTAGRAM_URL } from "@/data/assets";

const LINKS = [
  { id: "parts", label: "Parts" },
  { id: "process", label: "Process" },
  { id: "fitment", label: "Fitment" },
  { id: "gallery", label: "Gallery" },
];

export const Nav = ({ scrollTo, onEnquire }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(`#${id}`);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 h-[72px] flex items-center justify-between">
          <button
            onClick={() => go("home")}
            className="flex items-center"
            data-testid="nav-logo"
            aria-label="Rayce Parts home"
          >
            <img
              src={IMAGES.logo}
              alt="Rayce Parts"
              className="h-6 md:h-7 w-auto"
            />
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="group relative font-mono-rp text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#e31e24] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              className="hidden md:grid place-items-center h-9 w-9 border border-white/15 text-white/70 hover:text-white hover:border-[#e31e24] transition-colors"
              data-testid="nav-instagram"
              aria-label="Instagram"
            >
              <Instagram size={16} />
            </a>
            <button
              onClick={() => onEnquire("")}
              className="btn-race hidden sm:inline-flex !py-2.5 !px-5 text-sm"
              data-testid="nav-enquire-btn"
            >
              Enquire
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              className="md:hidden grid place-items-center h-10 w-10 text-white"
              data-testid="nav-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden bg-[#0a0a0a]/97 backdrop-blur-lg pt-24 px-6"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.id)}
                  data-testid={`mobile-link-${l.id}`}
                  className="font-display text-5xl text-white text-left py-3 border-b border-white/10"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onEnquire("");
                }}
                className="btn-race mt-8 justify-center"
                data-testid="mobile-enquire-btn"
              >
                Enquire About a Part
              </button>
              <a
                href="/admin"
                className="mt-6 flex items-center gap-2 font-mono-rp text-[11px] tracking-[0.25em] uppercase text-white/40 hover:text-[#e31e24] transition-colors"
                data-testid="mobile-admin-link"
              >
                <Lock size={12} /> Admin Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
